import { Injectable, Inject } from '@angular/core';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { PermitVarianceService } from './permit-variance.service';
import { PermitAopService, PermitAnnualOperatingPlanApi } from './permit-aop.service';
import { PermitApplicationService } from './permit-application.service';
import { PodsService } from './pods.service';
import { AppropriatorsService } from './appropriators.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class ApplicationPackageService {

  private applicationPackageSource = new BehaviorSubject<ApplicationPackage>(null);
  currentApplicationPackage = this.applicationPackageSource.asObservable();

  private dssInstructionsSource = new BehaviorSubject<DSSInstructionsApi>(null);
  dssInstructions = this.dssInstructionsSource.asObservable();
  applicationPackage: ApplicationPackage;
  submitWaterRightsIdResponse: number;

  constructor(
    @Inject('BASE_URL') private baseUrl: string,
    private http: HttpClient,
    private location: Location,
    private appropriatorsService: AppropriatorsService,
    private podsService: PodsService,
    private varianceService: PermitVarianceService,
    private applicationService: PermitApplicationService,
    private aopService: PermitAopService,
    private toastrService: ToastrService) { }

  getPackage(applicationPackageId: number) {
    this.http.get<Response>(`${this.baseUrl}api/applicationpackage/${applicationPackageId}`).subscribe(
      response => {
        if (response) {
          let data: any = response;
          this.changePackage(data);
          if (data.appropriatorId)              { this.appropriatorsService.getAppropriator(data.appropriatorId);   }
          else                                  { this.appropriatorsService.changeAppropriator(null);               }
          if (data.permitPointOfDiversionId)    { this.podsService.getPodByPodId(data.permitPointOfDiversionId);    }
          else                                  { this.podsService.changePodByPodId(null);                          }
          if (data.permitVarianceId)            { this.varianceService.getVariance(data.permitVarianceId);          }
          else                                  { this.varianceService.changeVariance(null);                        }
          if (data.permitApplicationId)         { this.applicationService.getApplication(data.permitApplicationId); }
          else                                  { this.applicationService.changeApplication(null);                  }
        }
      },
      error => console.error(error)
    );
  }

  postAop(appropriatorId: number, podId: number, createMode: number, fromPermitYear?: number) {
    let data: any = {};
    data.AppropriatorId = appropriatorId;
    data.PermitPointOfDiversionId = podId;
    let api = `${this.baseUrl}api/permitannualoperatingplan`;
    if (createMode == 2) {
      api = `${this.baseUrl}api/permitannualoperatingplan/newfromtemplate`;
      data.fromPermitYear = fromPermitYear;
    }
    this.http.post<PermitAnnualOperatingPlanApi>(api, data).subscribe(
      response => {
        if (response) {
          this.changePackage(null);
          this.getPackage(response.applicationPackageId);
          this.appropriatorsService.changeAppropriator(null);
          this.podsService.changePodByPodId(null);
          this.varianceService.changeVariance(null);
          this.aopService.getAop(response.permitAnnualOperatingPlanId);
          this.location.replaceState(`/permit-status/${response.applicationPackageId}/${response.permitAnnualOperatingPlanId}`);
        }
      },
      error => console.error(error)
    );
  }

  submitPackage(action: string, comment: string = '') {
    let data: any = this.applicationPackage;
    this.http.post(`${this.baseUrl}api/applicationpackage/${data.applicationPackageId}/workflow`, { workflowAction: action, comment: comment }, {observe: 'response'}).subscribe(response => {
      if (response.status === 200) {
          this.toastrService.success(`Successfully executed the '${action}' workflow for this Application package!`, 'Success:', {
          positionClass: 'toast-top-full-width',
          closeButton: true,
        });
        this.getPackage(data.applicationPackageId);
        this.aopService.getAop();
      }
    });
  }

  submitModificationId(modificationId: string) {
    let data: ApplicationPackageModificationIdApi = {
      applicationPackageId: this.applicationPackage.applicationPackageId,
      modificationId: modificationId
    };
    this.http.post(`${this.baseUrl}api/applicationpackage/SetModificationId`, data, { observe: 'response' }).subscribe(response => {
      if (response.status === 200) {
        this.submitPackage('Accept');
      }
    });
  }

  getDSSInstruction() {
    this.http.get<DSSInstructionsApi>(`${this.baseUrl}api/dssinstruction`, { observe: 'response' }).subscribe(response => {
        if (response.status === 200)
        {
          this.changeDssInstructions(response.body);
        }
    });
  }

  changePackage(applicationPackage: ApplicationPackage) {
    this.applicationPackage = applicationPackage;
    this.applicationPackageSource.next(applicationPackage);
  }

  changeDssInstructions(instructions: DSSInstructionsApi) {
    this.dssInstructionsSource.next(instructions);
  }

}

export interface ApplicationPackage {
  applicationPackageId: number,
  applicationNumber: string,
  waterRightsId: string,
  modificationId: number,
  canEdit: boolean,
  appropriatorId: number,
  permitVarianceId: number,
  permitApplicationId: number,
  permitAnnualOperatingPlanIds: number[],
  permitPointOfDiversionId: number,
  permitStatusId: number,
  permitStartDate?: Date,
  permitEndDate?: Date,
  permitStatusName: string,
  permitTypeName: string,
  allowedWorkflowActions: WorkflowAction[],
  submittedUserName: string,
  submittedTimestamp?: Date,
  acceptedUserName: string,
  acceptedTimestamp?: Date,
  approvedUserName: string,
  approvedTimestamp?: Date
}

export interface WorkflowAction {
  displayName: string,
  value: string
}

export interface ApplicationPackageModificationIdApi {
  applicationPackageId: number, // int
  modificationId: string
}

export interface DSSInstructionsApi {
  applicationRoute: string,
  applicationStartMonth: number,
  applicationStartDay: number,
  applicationEndMonth: number,
  applicationEndDay: number,
  applicationApprovalMonth: number,
  applicationApprovalDay: number,
  applicationYear: number,
}

export enum ApplicationPackageWorkflowAction {
  Submit = "Submit",
  Accept = "Accept",
  DeclineAcceptance = "DeclineAcceptance",
  Approve = "Approve",
  Deny = "Deny",
  ResetApplication = "ResetApplication",
  Withdraw = "Withdraw",
  ReopenApplication = "ReopenApplication"
}
